import React, { useState } from 'react';
import { supabase } from './supabase';
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff, Check } from 'lucide-react';
import { passwordStrength, passwordsMatch, getPasswordRequirements } from './utils/validation';
import { colors as theme } from './theme';

const colors = {
  primary: theme.gold,
  primaryDark: theme.goldPressed,
  success: theme.success,
  danger: theme.danger,
  bg: theme.bg,
  bgCard: theme.surface1,
  border: theme.borderStrong,
  text: theme.text,
  textMuted: theme.textMuted,
};

const ResetPassword = ({ onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password strength
    const strengthError = passwordStrength(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }

    // Validate passwords match
    const matchError = passwordsMatch(password, confirmPassword);
    if (matchError) {
      setError(matchError);
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      setSuccess(true);
    } catch (error) {
      console.error('Password update error:', error);
      setError(error.message || 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'Inter', sans-serif",
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
      }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: colors.text,
            margin: '0 0 8px 0',
          }}>
            {success ? 'Password Updated!' : 'Set New Password'}
          </h1>
          <p style={{
            fontSize: '15px',
            color: colors.textMuted,
            margin: 0,
          }}>
            {success
              ? 'Your password has been successfully updated'
              : 'Enter your new password below'
            }
          </p>
        </div>

        <div style={{
          background: colors.bgCard,
          padding: '32px',
          borderRadius: '20px',
          border: `1px solid ${colors.border}`,
        }}>
          {success ? (
            /* Success State */
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: `${colors.success}20`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <CheckCircle size={32} color={colors.success} />
              </div>
              <p style={{
                fontSize: '14px',
                color: colors.textMuted,
                margin: '0 0 24px 0',
                lineHeight: 1.5,
              }}>
                You can now log in with your new password.
              </p>
              <button
                onClick={onComplete}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                  border: 'none',
                  borderRadius: '14px',
                  color: theme.bg,
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Go to Login
              </button>
            </div>
          ) : (
            /* Password Form */
            <form onSubmit={handleSubmit}>
              {/* New Password */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: '8px',
                }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock
                    size={18}
                    color={colors.textMuted}
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 10 characters"
                    required
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '14px 48px 14px 48px',
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '15px',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {showPassword ? (
                      <EyeOff size={18} color={colors.textMuted} />
                    ) : (
                      <Eye size={18} color={colors.textMuted} />
                    )}
                  </button>
                </div>
                {/* Password requirements checklist */}
                <div style={{ marginTop: '12px' }}>
                  {getPasswordRequirements(password).map((req, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px',
                    }}>
                      <div style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        background: req.met ? colors.success : colors.border,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s',
                      }}>
                        {req.met && <Check size={8} color={theme.text} />}
                      </div>
                      <span style={{
                        fontSize: '13px',
                        color: req.met ? colors.success : colors.textMuted,
                        transition: 'color 0.2s',
                      }}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Confirm Password */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: '8px',
                }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock
                    size={18}
                    color={colors.textMuted}
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 48px 14px 48px',
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '15px',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} color={colors.textMuted} />
                    ) : (
                      <Eye size={18} color={colors.textMuted} />
                    )}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  padding: '12px 16px',
                  background: `${colors.danger}20`,
                  border: `1px solid ${colors.danger}`,
                  borderRadius: '8px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <AlertCircle size={18} color={colors.danger} />
                  <span style={{
                    fontSize: '14px',
                    color: colors.danger,
                  }}>
                    {error}
                  </span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: isLoading
                    ? colors.border
                    : `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                  border: 'none',
                  borderRadius: '14px',
                  color: theme.text,
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {isLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
